import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_IMAGES, DEMO_VIDEOS, DEMO_FRAME_COUNT } from "../mock-data";
import { useScrollFrameSequence } from "../../useScrollFrameSequence";

gsap.registerPlugin(ScrollTrigger);

/**
 * MIRAGE — Liquid Chrome / Type-as-Mask
 *
 * Pure black + animated chrome gradient + electric violet (#a259ff).
 * Massive serif title rendered as flowing liquid chrome (text-as-mask
 * with animated gradient). Mirror cards with reflection. Type-as-photo-mask
 * (huge letters with image inside via background-clip). Floating chrome bars.
 *
 *  ACT I   HERO — Chrome title (animated linear-gradient through serif),
 *          mirror floor reflection of title, 4 chrome bars float in
 *          with rotateY perspective.
 *  ACT II  PINNED TYPE-AS-MASK — A giant letter "M" (or & or year) fills
 *          screen, with photo cycling inside the letter via clip-path.
 *          As you scroll, letters transition: M → & → Z (each holding
 *          its own image scroll-synced).
 *  ACT III MIRROR GALLERY — 5 photo cards with their REFLECTION
 *          underneath (flipped + faded + blurred). Cards float upward
 *          with parallax at different speeds.
 *  ACT IV  CHROME DATE — date numbers rendered as animated chrome,
 *          violet bar accent draws across. SVG distortion glow.
 *  ACT V   PINNED CINEMA STRIP — full video plays scroll-synced with
 *          chrome text ticker crossing in front + chromatic split.
 *  ACT VI  RSVP card with chrome border + magnetic 3D tilt.
 */
export function MirageLayout({ formSlot, program, title, dateTime, location, description, galleryImages }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);
  const tiltRef = useRef<HTMLDivElement>(null);
  const cinSectionRef = useRef<HTMLElement | null>(null);
  const cinCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Act V cinema strip: Apple-style frame sequence (replaces <video>.currentTime).
  useScrollFrameSequence({
    containerRef: cinSectionRef,
    canvasRef: cinCanvasRef,
    source: { kind: "video", url: DEMO_VIDEOS.timelineScroll, count: DEMO_FRAME_COUNT },
    pinViewports: 3,
  });

  const d = dateTime ? new Date(dateTime) : null;
  const day = d?.getDate().toString().padStart(2, "0") ?? "--";
  const monthShort = d?.toLocaleString("en-US", { month: "short" }).toUpperCase() ?? "—";
  const monthNum = d ? (d.getMonth() + 1).toString().padStart(2, "0") : "--";
  const year = d?.getFullYear() ?? 2026;

  // First and last letter of title (used for type-as-mask)
  const [firstName, lastName] = title.split("&").map((s) => s.trim());
  const firstLetter = (firstName?.[0] ?? "M").toUpperCase();
  const lastLetter = (lastName?.[0] ?? "Z").toUpperCase();

  useEffect(() => {
    const card = tiltRef.current;
    if (!card) return;
    const handleMove = (e: MouseEvent) => {
      const r = card.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      card.style.transform = `perspective(1200px) rotateY(${dx * 10}deg) rotateX(${-dy * 10}deg)`;
    };
    const reset = () => { card.style.transform = "perspective(1200px) rotateY(0) rotateX(0)"; };
    window.addEventListener("mousemove", handleMove);
    card.addEventListener("mouseleave", reset);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      card.removeEventListener("mouseleave", reset);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.5, easing: (t) => 1 - Math.pow(1 - t, 5) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      // ── ACT I: HERO ────────────────────────────────────────────────
      const tl = gsap.timeline({ defaults: { ease: "expo.out" } });

      tl.from(".mir-marquee-t", { x: "100%", duration: 1.6 }, 0);
      tl.from(".mir-marquee-b", { x: "-100%", duration: 1.6 }, 0);

      // Title — animate as a whole to preserve background-clip:text chrome effect
      // (SplitText breaks chrome because each char div doesn't inherit the gradient bg)
      tl.from(".mir-hero-title", {
        y: 80,
        scale: 0.96,
        opacity: 0,
        duration: 1.6,
        ease: "expo.out",
      }, 0.4);

      tl.from(".mir-hero-refl", {
        y: -40,
        opacity: 0,
        duration: 1.6,
        ease: "expo.out",
      }, 0.7);

      // 4 chrome bars float in from sides
      tl.from(".mir-chrome-bar-tl", { x: -200, rotateY: -45, opacity: 0, duration: 1.4 }, 0.6);
      tl.from(".mir-chrome-bar-tr", { x: 200, rotateY: 45, opacity: 0, duration: 1.4 }, 0.7);
      tl.from(".mir-chrome-bar-bl", { x: -200, rotateY: 45, opacity: 0, duration: 1.4 }, 0.8);
      tl.from(".mir-chrome-bar-br", { x: 200, rotateY: -45, opacity: 0, duration: 1.4 }, 0.9);

      tl.from(".mir-hero-meta", { y: 20, opacity: 0, stagger: 0.1, duration: 1 }, 1.2);

      // Continuous gentle drift on chrome bars
      gsap.to(".mir-chrome-bar", { y: "+=10", rotate: "+=2", duration: 4, ease: "sine.inOut", repeat: -1, yoyo: true, stagger: 0.5 });

      // ── ACT II: PINNED TYPE-AS-MASK (3 letters) ────────────────────
      const maskSection = document.querySelector<HTMLElement>(".mir-mask-section");
      if (maskSection) {
        const maskTl = gsap.timeline({
          scrollTrigger: {
            trigger: maskSection,
            start: "top top",
            end: "+=300%",
            pin: true,
            scrub: 1,
          },
        });

        // Letter 1 → Letter 2 → Letter 3 transitions
        // Each letter cross-fades to next, with image scaling inside
        maskTl
          .to(".mir-mask-letter-1", { opacity: 0, scale: 0.85, duration: 1 }, 1)
          .to(".mir-mask-letter-2", { opacity: 1, scale: 1, duration: 1 }, 1)
          .to(".mir-mask-letter-2 .mir-mask-img", { scale: 1.2, duration: 2, ease: "none" }, 1)
          .to(".mir-mask-letter-2", { opacity: 0, scale: 0.85, duration: 1 }, 2.5)
          .to(".mir-mask-letter-3", { opacity: 1, scale: 1, duration: 1 }, 2.5);

        // First letter image zoom on initial
        gsap.to(".mir-mask-letter-1 .mir-mask-img", {
          scale: 1.2,
          ease: "none",
          scrollTrigger: {
            trigger: maskSection,
            start: "top top",
            end: "+=100%",
            scrub: 1,
          },
        });

        gsap.to(".mir-mask-letter-3 .mir-mask-img", {
          scale: 1.2,
          ease: "none",
          scrollTrigger: {
            trigger: maskSection,
            start: "top top-=200%",
            end: "+=100%",
            scrub: 1,
          },
        });
      }

      // ── ACT III: MIRROR GALLERY ───────────────────────────────────
      document.querySelectorAll(".mir-mirror-card").forEach((card, i) => {
        gsap.from(card, {
          y: 80,
          opacity: 0,
          rotateX: -20,
          duration: 1.4,
          delay: i * 0.1,
          ease: "expo.out",
          scrollTrigger: { trigger: ".mir-mirror-section", start: "top 75%", toggleActions: "play reverse play reverse" },
        });
        // Parallax different speeds
        gsap.to(card, {
          y: -50 - i * 10,
          ease: "none",
          scrollTrigger: { trigger: ".mir-mirror-section", start: "top bottom", end: "bottom top", scrub: 1 + i * 0.2 },
        });
      });

      gsap.from(".mir-mirror-title", { y: 50, opacity: 0, duration: 1.2, ease: "expo.out",
        scrollTrigger: { trigger: ".mir-mirror-section", start: "top 75%", toggleActions: "play reverse play reverse" } });

      // ── ACT IV: CHROME DATE ───────────────────────────────────────
      const dayEl = document.querySelector<HTMLElement>(".mir-num-day");
      const monthEl = document.querySelector<HTMLElement>(".mir-num-month");
      if (dayEl && monthEl && d) {
        const obj = { d: 0, m: 0 };
        gsap.to(obj, {
          d: d.getDate(), m: d.getMonth() + 1,
          duration: 1.5, ease: "power3.out",
          scrollTrigger: { trigger: ".mir-date-section", start: "top 70%", toggleActions: "play reverse play reverse" },
          onUpdate() {
            dayEl.textContent = Math.round(obj.d).toString().padStart(2, "0");
            monthEl.textContent = Math.round(obj.m).toString().padStart(2, "0");
          },
        });
      }
      gsap.from(".mir-date-bar", {
        scaleX: 0,
        transformOrigin: (i) => i === 0 ? "left" : "right",
        duration: 1.4,
        ease: "expo.inOut",
        scrollTrigger: { trigger: ".mir-date-section", start: "top 65%", toggleActions: "play reverse play reverse" },
      });
      gsap.from(".mir-date-glow", {
        opacity: 0,
        scale: 0.7,
        duration: 1.6,
        ease: "expo.out",
        scrollTrigger: { trigger: ".mir-date-section", start: "top 65%", toggleActions: "play reverse play reverse" },
      });

      // ── ACT V: PINNED CINEMA STRIP — frame-sequence handled by hook ──
      const cinSection = document.querySelector<HTMLElement>(".mir-cinema-section");
      if (cinSection) {
        // Chrome text tickers + chromatic aberration sync off the same pin
        // distance (3 viewports — must match `pinViewports` in useScrollFrameSequence)
        gsap.fromTo(".mir-cin-tick-1", { x: "-100%" }, { x: "100%", ease: "none",
          scrollTrigger: { trigger: cinSection, start: "top top", end: "+=300%", scrub: 1 } });
        gsap.fromTo(".mir-cin-tick-2", { x: "100%" }, { x: "-100%", ease: "none",
          scrollTrigger: { trigger: cinSection, start: "top top", end: "+=300%", scrub: 1 } });

        gsap.fromTo(".mir-aberr-r", { x: 0 }, { x: -30, ease: "none",
          scrollTrigger: { trigger: cinSection, start: "top top", end: "+=300%", scrub: 1 } });
        gsap.fromTo(".mir-aberr-b", { x: 0 }, { x: 30, ease: "none",
          scrollTrigger: { trigger: cinSection, start: "top top", end: "+=300%", scrub: 1 } });
      }

      // Form
      gsap.from(".mir-form-card", { opacity: 0, y: 60, rotateX: -15, duration: 1.4, ease: "expo.out",
        scrollTrigger: { trigger: ".mir-form-card", start: "top 80%", toggleActions: "play reverse play reverse" } });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
    };
  }, []);

  return (
    <main className="relative overflow-x-hidden text-white" style={{ background: "#050307", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @keyframes mirChromeFlow {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        .mir-chrome-text {
          background: linear-gradient(110deg,
            #888 0%, #fff 15%, #aaa 30%, #f0f0f0 45%, #999 55%,
            #fff 65%, #c0c0c0 80%, #fff 95%, #888 100%
          );
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: mirChromeFlow 6s linear infinite;
          text-shadow: 0 0 80px rgba(255,255,255,0.1);
        }
        .mir-chrome-bar-bg {
          background: linear-gradient(180deg, #555 0%, #fff 25%, #888 50%, #fff 75%, #444 100%);
          background-size: 100% 200%;
          animation: mirChromeFlow 8s linear infinite;
        }
        @keyframes mirMQ_L { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes mirMQ_R { from { transform: translateX(-50%); } to { transform: translateX(0); } }
      `}</style>

      {/* Background gradient */}
      <div className="fixed inset-0 -z-30 pointer-events-none" style={{
        background: "radial-gradient(ellipse at 10% 20%, #1a0e2a 0%, transparent 50%), radial-gradient(ellipse at 90% 80%, #0e1a2a 0%, transparent 50%), #050307",
      }} />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-30 px-8 py-4 flex justify-between items-center text-[10px] uppercase tracking-[0.5em]"
        style={{ background: "rgba(5, 3, 7, 0.6)", backdropFilter: "blur(10px)", borderBottom: "1px solid rgba(162, 89, 255, 0.2)" }}>
        <span className="mir-chrome-text">Mirage</span>
        <span className="text-white/40">{monthShort} {day}</span>
        <span style={{ color: "#a259ff" }}>● {year}</span>
      </header>

      {/* ═══ ACT I: HERO ═══════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col justify-between overflow-hidden">
        {/* Top marquee */}
        <div className="mir-marquee-t relative h-10 border-b border-white/10 overflow-hidden flex items-center mt-12">
          <div className="whitespace-nowrap text-[10px] tracking-[0.5em] uppercase" style={{ animation: "mirMQ_L 32s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">
                <span className="mir-chrome-text font-bold">{title.toUpperCase()}</span> · <span style={{ color: "#a259ff" }}>◆</span> · MIRAGE · <span style={{ color: "#a259ff" }}>◆</span> · {monthShort} {day} {year} · <span style={{ color: "#a259ff" }}>◆</span> ·
              </span>
            ))}
          </div>
        </div>

        {/* Center stage */}
        <div className="flex-1 flex flex-col items-center justify-center relative px-6" style={{ perspective: 1600 }}>
          {/* Chrome bars float in 4 corners */}
          <div className="mir-chrome-bar mir-chrome-bar-tl absolute top-16 left-12 w-2 h-32 mir-chrome-bar-bg hidden md:block" style={{ boxShadow: "0 20px 40px rgba(255,255,255,0.1)" }} />
          <div className="mir-chrome-bar mir-chrome-bar-tr absolute top-16 right-12 w-2 h-32 mir-chrome-bar-bg hidden md:block" style={{ boxShadow: "0 20px 40px rgba(255,255,255,0.1)" }} />
          <div className="mir-chrome-bar mir-chrome-bar-bl absolute bottom-32 left-12 w-2 h-48 mir-chrome-bar-bg hidden md:block" style={{ boxShadow: "0 20px 40px rgba(255,255,255,0.1)" }} />
          <div className="mir-chrome-bar mir-chrome-bar-br absolute bottom-32 right-12 w-2 h-48 mir-chrome-bar-bg hidden md:block" style={{ boxShadow: "0 20px 40px rgba(255,255,255,0.1)" }} />

          <p className="mir-hero-meta text-[10px] uppercase tracking-[0.6em] text-white/40 mb-6">— A Liquid Edition —</p>

          {/* Chrome title */}
          <h1
            className="mir-hero-title mir-chrome-text text-center font-light leading-[0.85] tracking-[-0.04em]"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: "clamp(4rem, 14vw, 13rem)",
            }}
          >
            {title}
          </h1>

          {/* Mirror reflection */}
          <div className="mir-hero-refl text-center font-light leading-[0.85] tracking-[-0.04em] mt-1 opacity-30"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: "clamp(4rem, 14vw, 13rem)",
              transform: "scaleY(-1)",
              maskImage: "linear-gradient(180deg, rgba(0,0,0,0.4), transparent 50%)",
              WebkitMaskImage: "linear-gradient(180deg, rgba(0,0,0,0.4), transparent 50%)",
              color: "#666",
              filter: "blur(2px)",
            }}
          >
            {title}
          </div>

          <div className="mir-hero-meta mt-12 flex items-center gap-6 text-xs uppercase tracking-[0.5em] text-white/70">
            <span>{monthShort}</span>
            <span style={{ color: "#a259ff" }}>◆</span>
            <span className="tabular-nums">{day}</span>
            <span style={{ color: "#a259ff" }}>◆</span>
            <span>{year}</span>
          </div>
          <p className="mir-hero-meta mt-3 italic text-white/40" style={{ fontFamily: "'Playfair Display', serif", fontSize: 18 }}>
            {location?.split(",")[0] ?? "—"}
          </p>
        </div>

        {/* Bottom marquee */}
        <div className="mir-marquee-b relative h-10 border-t border-white/10 overflow-hidden flex items-center">
          <div className="whitespace-nowrap text-[10px] tracking-[0.5em] uppercase" style={{ color: "#a259ff", animation: "mirMQ_R 38s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">◇ LIQUID FORM ◇ MIRROR FINISH ◇ {(location ?? "").toUpperCase()} ◇</span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT II: PINNED TYPE-AS-MASK ═════════════════════════════ */}
      <section className="mir-mask-section relative h-screen overflow-hidden bg-black">
        <div className="absolute top-12 left-12 text-[10px] uppercase tracking-[0.5em] text-white/40">II · MIRROR LETTERS</div>
        <div className="absolute top-12 right-12 text-[10px] uppercase tracking-[0.5em]" style={{ color: "#a259ff" }}>SCROLL</div>

        {/* 3 letter layers — each contains a clipped image */}
        {[
          { letter: firstLetter, img: galleryImages[0], cls: "mir-mask-letter-1" },
          { letter: "&", img: galleryImages[2], cls: "mir-mask-letter-2" },
          { letter: lastLetter, img: galleryImages[4], cls: "mir-mask-letter-3" },
        ].map((l, i) => (
          <div
            key={i}
            className={`${l.cls} absolute inset-0 flex items-center justify-center`}
            style={{ opacity: i === 0 ? 1 : 0 }}
          >
            {/* Letter mask using SVG */}
            <svg viewBox="0 0 800 800" className="w-full h-full" style={{ maxWidth: "min(80vw, 80vh)", maxHeight: "min(80vw, 80vh)" }}>
              <defs>
                <clipPath id={`mask-clip-${i}`}>
                  <text
                    x="400"
                    y="650"
                    textAnchor="middle"
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: 800,
                      fontWeight: 300,
                    }}
                  >
                    {l.letter}
                  </text>
                </clipPath>
              </defs>
              <g clipPath={`url(#mask-clip-${i})`}>
                <image
                  className="mir-mask-img"
                  href={l.img}
                  x="-100" y="-100" width="1000" height="1000"
                  preserveAspectRatio="xMidYMid slice"
                />
              </g>
              {/* Stroke outline for chrome effect */}
              <text
                x="400" y="650" textAnchor="middle"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 800,
                  fontWeight: 300,
                  fill: "none",
                  stroke: "rgba(255,255,255,0.15)",
                  strokeWidth: 1,
                }}
              >{l.letter}</text>
            </svg>

            {/* Caption */}
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-center">
              <p className="text-[10px] uppercase tracking-[0.6em] mb-2" style={{ color: "#a259ff" }}>
                Letter {["I", "II", "III"][i]}
              </p>
              <p className="italic font-light" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.25rem, 2.5vw, 2rem)" }}>
                {["İlk harf, ilk söz", "İki dünya bir oluyor", "Son harf, son söz"][i]}
              </p>
            </div>
          </div>
        ))}
      </section>

      {/* ═══ ACT III: MIRROR GALLERY ═════════════════════════════════ */}
      <section className="mir-mirror-section relative py-32 px-6 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="mir-mirror-title mb-16">
            <p className="text-center text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#a259ff" }}>III · REFLECTIONS</p>
            <h2 className="text-center font-light leading-tight mir-chrome-text" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
              Five mirrors,<br />one story.
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" style={{ perspective: 1600 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="mir-mirror-card relative" style={{ transformStyle: "preserve-3d" }}>
                {/* Original card */}
                <div className="aspect-[3/4] overflow-hidden relative" style={{
                  border: "1px solid rgba(255,255,255,0.15)",
                  boxShadow: "0 30px 50px rgba(0,0,0,0.5)",
                }}>
                  <img src={galleryImages[i]} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(0.4) contrast(1.05)" }} />
                  <div className="absolute top-2 left-2 text-[9px] tracking-[0.3em] uppercase mir-chrome-text font-bold">0{i + 1}</div>
                </div>
                {/* Mirror reflection underneath */}
                <div className="aspect-[3/4] overflow-hidden relative" style={{
                  transform: "scaleY(-1)",
                  maskImage: "linear-gradient(180deg, rgba(0,0,0,0.6), transparent 60%)",
                  WebkitMaskImage: "linear-gradient(180deg, rgba(0,0,0,0.6), transparent 60%)",
                }}>
                  <img src={galleryImages[i]} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(0.4) contrast(1.05) blur(1px)" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT IV: CHROME DATE ══════════════════════════════════════ */}
      <section className="mir-date-section relative py-32 px-6 overflow-hidden">
        {/* Violet ambient glow */}
        <div className="mir-date-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] max-w-3xl max-h-3xl pointer-events-none" style={{
          background: "radial-gradient(circle, rgba(162, 89, 255, 0.2) 0%, transparent 60%)",
          filter: "blur(40px)",
        }} />

        <div className="relative max-w-5xl mx-auto text-center">
          <p className="text-[10px] uppercase tracking-[0.6em] mb-12" style={{ color: "#a259ff" }}>IV · THE DATE</p>

          <div className="flex items-center justify-center gap-4 sm:gap-12">
            <div className="mir-date-bar h-px flex-1" style={{ background: "linear-gradient(90deg, transparent, #a259ff)" }} />
            <p className="mir-num-day mir-chrome-text font-light tabular-nums leading-none tracking-[-0.05em]"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(5rem, 14vw, 12rem)" }}>00</p>
            <div className="font-light text-white/30 leading-none" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(3rem, 9vw, 8rem)" }}>·</div>
            <p className="mir-num-month mir-chrome-text font-light tabular-nums leading-none tracking-[-0.05em]"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(5rem, 14vw, 12rem)" }}>00</p>
            <div className="mir-date-bar h-px flex-1" style={{ background: "linear-gradient(-90deg, transparent, #a259ff)" }} />
          </div>

          <p className="mt-6 text-2xl tabular-nums tracking-widest text-white/70" style={{ fontFamily: "'Playfair Display', serif" }}>{year}</p>
          <p className="mt-6 text-xs uppercase tracking-[0.5em]" style={{ color: "#a259ff" }}>
            ● {d?.toLocaleString("en-US", { weekday: "long" }).toUpperCase()} · {d?.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })} ●
          </p>
        </div>
      </section>

      {/* ═══ ACT V: PINNED CINEMA STRIP ═══════════════════════════════ */}
      <section
        ref={cinSectionRef as React.RefObject<HTMLElement>}
        className="mir-cinema-section relative h-screen overflow-hidden"
      >
        <canvas
          ref={cinCanvasRef}
          className="mir-cinema-canvas absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30" />

        {/* Chromatic aberration title */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative">
            <h2 className="mir-aberr-r absolute inset-0 font-light italic tracking-[-0.04em]"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(3rem, 8vw, 7rem)", color: "rgba(255, 80, 200, 0.7)", mixBlendMode: "screen" }}>
              {title}
            </h2>
            <h2 className="mir-aberr-b absolute inset-0 font-light italic tracking-[-0.04em]"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(3rem, 8vw, 7rem)", color: "rgba(80, 200, 255, 0.7)", mixBlendMode: "screen" }}>
              {title}
            </h2>
            <h2 className="relative mir-chrome-text font-light italic tracking-[-0.04em]"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(3rem, 8vw, 7rem)" }}>
              {title}
            </h2>
          </div>
        </div>

        {/* Tickers */}
        <div className="mir-cin-tick-1 absolute top-[15%] left-0 right-0 whitespace-nowrap pointer-events-none mir-chrome-text"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 6vw, 5rem)", fontWeight: 300, fontStyle: "italic" }}>
          {Array(4).fill(`MIRAGE · ${monthShort} ${day} ${year} ·  `).join("")}
        </div>
        <div className="mir-cin-tick-2 absolute bottom-[15%] left-0 right-0 whitespace-nowrap pointer-events-none text-right"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 6vw, 5rem)", fontWeight: 300, color: "#a259ff" }}>
          {Array(4).fill(`◇ ${title.toUpperCase()} ·  `).join("")}
        </div>

        <div className="absolute top-12 left-12 text-[10px] uppercase tracking-[0.5em] text-white/40">V · ABERRATION</div>
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.6em]" style={{ color: "#a259ff" }}>SCROLL → ADVANCE TIME</div>
      </section>

      {/* ═══ ACT VI: RSVP ═════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-32" style={{ perspective: 1200 }}>
        <div
          ref={tiltRef}
          className="mir-form-card w-full max-w-md p-12 relative transition-transform duration-300 ease-out"
          style={{
            background: "linear-gradient(135deg, rgba(162, 89, 255, 0.08) 0%, rgba(162, 89, 255, 0.02) 100%)",
            border: "1px solid rgba(162, 89, 255, 0.3)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.15), 0 40px 100px rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(20px)",
            transformStyle: "preserve-3d",
          }}
        >
          {/* Chrome border accent on top */}
          <div className="absolute top-0 left-8 right-8 h-px mir-chrome-bar-bg" />

          <div className="absolute top-3 left-3 w-5 h-5 border-t border-l" style={{ borderColor: "#a259ff" }} />
          <div className="absolute top-3 right-3 w-5 h-5 border-t border-r" style={{ borderColor: "#a259ff" }} />
          <div className="absolute bottom-3 left-3 w-5 h-5 border-b border-l" style={{ borderColor: "#a259ff" }} />
          <div className="absolute bottom-3 right-3 w-5 h-5 border-b border-r" style={{ borderColor: "#a259ff" }} />

          <p className="text-center text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#a259ff" }}>VI · REFLECTION</p>
          <h3 className="text-center mir-chrome-text font-light italic mb-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3rem)" }}>
            Your reply
          </h3>
          <div className="mx-auto w-12 h-px mb-10" style={{ background: "#a259ff" }} />
          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
        </div>
      </section>
    </main>
  );
}
